<template>
  <div id="previewer">
    <div class="bar">
      <button @click="back" class="action" :title="$t('files.closePreview')" :aria-label="$t('files.closePreview')" id="close">
        <XIcon stroke="white"/>
      </button>
      <download-button></download-button>
      <info-button></info-button>
    </div>

    <button class="action" @click="prev" v-show="hasPrevious" :aria-label="$t('buttons.previous')" :title="$t('buttons.previous')">
      <i class="material-icons">chevron_left</i>
    </button>
    <button class="action" @click="next" v-show="hasNext" :aria-label="$t('buttons.next')" :title="$t('buttons.next')">
      <i class="material-icons">chevron_right</i>
    </button>

    <div class="preview" id="previewDiv">
      <h2 class="message" v-if="!uri">{{ $t('info.loading') }}</h2>
      <template v-else>
        <img v-if="req.type == 'image'" :src="uri" style="max-width: 100%; min-width: 10%;">
        <audio v-else-if="req.type == 'audio'" :src="uri" autoplay controls></audio>
        <video v-else-if="req.type == 'video'" :src="uri" autoplay controls>
          <font color="white">Sorry, your browser doesn't support embedded videos,
          but don't worry, you can <a :download="nodeName" :href="uri">download it</a>
          and watch it with your favorite video player!</font>
        </video>
        <object v-else-if="req.type == 'pdf'" class="pdf" :data="uri" type="application/pdf" style="overflow-y: auto; height: 100%; width: 100%">
        </object>
        <template v-else-if="req.type == 'stl'">
          <model-stl v-if="uri && uri !== ''" :src="uri" :backgroundColor="0xffffff"></model-stl>
          <font color="white" v-else>{{ $t('info.loading') }}</font>
        </template>
        <a v-else :download="nodeName" :href="uri">
          <h2 class="message">{{ $t('buttons.download') }} <i class="material-icons">file_download</i></h2>
        </a>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { files as api } from '@/api';
import InfoButton from '@/components/buttons/Info.vue';
import DownloadButton from '@/components/buttons/Download.vue';
import { ModelStl } from 'vue-3d-model';
import { ROUTES } from '@/router';
import { XIcon } from 'vue-feather-icons';

const mediaTypes = [
  'image',
  'video',
  'audio',
  'pdf',
  'stl',
  'blob'
];

export default {
  name: 'preview',
  components: {
    DownloadButton,
    InfoButton,
    ModelStl,
    XIcon,
},
  data: function () {
    return {
      previousLink: '',
      nextLink: '',
      listing: null,
      node: null,
      uri: '',
    };
  },
  computed: {
  ...mapGetters([
      'selectedCount',
      'isFiles',
      'isBackup',
      'isRegister',
      'isEditor',
      'isTransfers',
      'isListing',
      'isLogged'
    ]),
    ...mapState(['req', 'user', 'oldReq', 'jwt', 'linkSharedDrive']),
    hasPrevious () {
      return (this.previousLink !== '');
    },
    hasNext () {
      return (this.nextLink !== '');
    },
    nodeName () {
      let path = `${this.req.path}`;
      path = decodeURIComponent(path);
      let fileName = path.replace(/^.*[\\/]/, '');
      return fileName;
    },
  },
  async mounted () {
    if (this.$route.name !== ROUTES.DRIVE) {
      this.node = await ibb.getNodeById(this.req.handle);
    } else {
      this.node = await ibbLink.getNodeById(this.req.handle);
    }
    this.uri = await this.getFile();
    
    window.addEventListener('keyup', this.key);

    try {
      if (this.oldReq.items) {
        this.updateLinks(this.oldReq.items);
      } else {
        if (this.$route.name !== ROUTES.DRIVE) {
          const res = await api.fetchId(this.req.parentHandle);
          this.updateLinks(res.items);
        } else {
          const res = await api.fetchId(this.req.parentHandle, Module.SortOrderType.SORT_ORDER_DEFAULT.value, ibbLink);
          this.updateLinks(res.items);
        }
      }
    } catch (e) {
      this.$showError(e);
    }
  },
  beforeDestroy () {
    window.removeEventListener('keyup', this.key);
  },
  methods: {
    ...mapMutations(['setIsPreview']),
    isMobile: function () {
      return window.innerWidth <= 768;
    },
    async back () {
      let handle;
      if (this.isRegister && !this.isMobile()) {
        let uri = this.$route.path;
        handle = await api.getParent(this.req.parentHandle);
        this.$router.replace({path: uri, query: {inbox :handle.parentHandle, open: this.req.parentHandle}});
        this.setIsPreview(true);
      } else if (this.isRegister && this.isMobile()) {
        try {
          handle = await api.getParent(this.req.parentHandle);
          let uri = this.$route.path + '#' + handle.parentHandle;
          this.$router.replace({ path: uri, query: {open: this.req.parentHandle} });
          this.setIsPreview(true);
        } catch (e) {
          this.$showError(e);
        }
      } else if (this.$route.name === ROUTES.DRIVE) {
        let parentNode = await ibbLink.getNodeById(this.req.parentHandle);
        let uri;
        if (parentNode.node.parentHandle === '') {
          uri = this.$route.path + '#' + this.linkSharedDrive;
        } else {
          uri = this.$route.path + '#' + this.req.parentHandle;
        }
        this.$router.push({ path: uri });
      } else {
        let uri = this.$route.path + '#' + this.req.parentHandle;
        this.$router.push({ path: uri });
      }
    },
    prev () {
      this.$router.push({hash: '#' + this.previousLink});
    },
    next () {
      this.$router.push({hash: '#' + this.nextLink});
    },
    key (event) {
      event.preventDefault();

      if (event.which === 13 || event.which === 39) { // right arrow
        if (this.hasNext) this.next();
      } else if (event.which === 37) { // left arrow
        if (this.hasPrevious) this.prev();
      }
    },
    async getFile() {
      let path = `${this.req.path}`;
      path = decodeURIComponent(path);
      let fileName = path.replace(/^.*[\\/]/, '');
      let action;
      if (this.$route.name !== ROUTES.DRIVE) {
        action = await ibb.getNodeByPath(path);
      } else {
        action = await ibbLink.getNodeByPath(path);
      }
      
      if (action.error.type) {
        throw new Error('Error getting node by path: ' + action.error.type);
      }

      let node = action.node;
      if (this.req.type === 'image') {
        if (this.$route.name !== ROUTES.DRIVE) {
          let previewName = '///previews/' + node.handle;
          let uri = Module.getObjectURL(previewName);
          if (!uri)
          {
            const actionMetadata = await ibb.downloadMetadata(node.handle, Module.MetadataType.META_PREVIEW.value, previewName);
            if (actionMetadata.error.type) {
              action = await ibb.downloadWithCustomData(node.handle, node.name, 'preview');
              let url = Module.getObjectURL(node.name);
              let file = Module.getFile(node.name);

              await new Promise (resolve => {
                Module.createMetadata(url, Module.MetadataType.META_PREVIEW.value, (data) => {
                  console.log('Preview created: ' + data.length + ' bytes');
                  Module.createFile(previewName, data);
                  ibb.uploadMetadata(previewName, node.handle, Module.MetadataType.META_PREVIEW.value);
                  resolve();
                }, file['orientation']);
              });
            }
          }
        } else {
          action = await ibbLink.downloadMetadata(this.req.handle, Module.MetadataType.META_PREVIEW.value, '///previews/' + this.req.handle);
        }
      } else {
        if (!Module.getObjectURL(node.handle + '/' + fileName)) { 
          if (this.$route.name !== ROUTES.DRIVE) {
            action = await ibb.downloadWithCustomData(node.handle, node.handle + '/' + fileName, 'preview');
          } else {
            action = await ibbLink.downloadWithCustomData(node.handle, node.handle + '/' + fileName, 'preview');
          }
        }
      }
      if (action.error.type) {
        this.$showError(action.error.type);
        return null;
      }

      let uri;
      if (this.req.type === 'image') {
        uri = Module.getObjectURL('///previews/' + this.req.handle);
      } else {
        uri = Module.getObjectURL(node.handle + '/' + fileName);
      }
      return uri;
    },
    updateLinks (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].name !== this.req.name) {
          continue;
        }

        for (let j = i - 1; j >= 0; j--) {
          if (mediaTypes.includes(items[j].type) && items[j].size <= 50*1024*1024) {
            this.previousLink = items[j].handle;
            break;
          }
        }

        for (let j = i + 1; j < items.length; j++) {
          if (mediaTypes.includes(items[j].type) && items[j].size <= 50*1024*1024) {
            this.nextLink = items[j].handle;
            break;
          }
        }

        return;
      }
    }
  }
};
</script>
